<template>
  <v-navigation-drawer
    v-model="drawerOpen"
    app
    color="#000000"
    class="sidebar"
    permanent
    disable-resize-watcher
    disable-route-watcher
    :width="expanded ? 180 : 60"
  >
    <!-- Sidebar Header -->
    <v-list class="sidebar-header">
      <v-list-item>
        <v-btn icon class="toggle-btn" @click.stop="toggleSidebar">
          <v-icon class="icon-small" color="#ffffff">mdi-menu</v-icon>
        </v-btn>
        <v-list-item-content v-if="expanded" class="header-content">
          <v-list-item-title class="header">Inventory</v-list-item-title>
        </v-list-item-content>
      </v-list-item>
      <v-divider class="divider"></v-divider>
    </v-list>

    <!-- Main Navigation -->
    <v-list class="navigation-links">
      <v-tooltip
        v-for="item in mainItems"
        :key="item.title"
        right
        :disabled="expanded"
      >
        <template #activator="{ on, attrs }">
          <v-list-item
            class="nav-item"
            v-bind="attrs"
            @click="handleClick(item.link)"
            v-on="on"
          >
            <v-list-item-icon>
              <v-icon class="icon-regular" color="#ffffff">{{
                item.icon
              }}</v-icon>
            </v-list-item-icon>
            <v-list-item-content v-if="expanded">
              <v-list-item-title class="item-title">
                {{ DIALECT[item.title][$vuetify.lang.current] }}
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </template>
        <span>{{ DIALECT[item.title][$vuetify.lang.current] }}</span>
      </v-tooltip>
    </v-list>

    <v-divider class="my-2 divider"></v-divider>

    <!-- Secondary Tools -->
    <v-list class="navigation-links secondary-tools footer">
      <v-tooltip
        v-for="tool in filteredSecondaryItems"
        :key="tool.title"
        right
        :disabled="expanded"
      >
        <template #activator="{ on, attrs }">
          <v-list-item
            class="nav-item small-item"
            v-bind="attrs"
            @click="handleClick(tool.link)"
            v-on="on"
          >
            <v-list-item-icon>
              <v-icon class="icon-small" color="#ffffff">{{
                tool.icon
              }}</v-icon>
            </v-list-item-icon>
            <v-list-item-content v-if="expanded">
              <v-list-item-title class="small-title">
                {{ DIALECT[tool.title][$vuetify.lang.current] }}
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </template>
        <span>{{ DIALECT[tool.title][$vuetify.lang.current] }}</span>
      </v-tooltip>
    </v-list>
  </v-navigation-drawer>
</template>

<script>
import DIALECT from '@/helpers/dialect.js'
import { mapGetters } from 'vuex'
import { TokenService } from '@/helpers/storage'

export default {
  name: 'SideBarComponent',
  data() {
    return {
      drawerOpen: true,
      expanded: false,
      DIALECT,
      // Reactive cache of current roles to avoid relying on non-reactive token/localStorage reads inside computed.
      userRoles: [],
      mainItems: [
        { title: 'Dashboard', link: '/dashboard', icon: 'mdi-view-dashboard' },
        { title: 'Items', link: '/items', icon: 'mdi-package-variant-closed' },
        { title: 'Reports', link: '/reports', icon: 'mdi-file-chart' },
        { title: 'Tags', link: '/tags', icon: 'mdi-tag' }
      ],
      secondaryItems: [
        { title: 'Finances', link: '/finances', icon: 'mdi-cash' },
        { title: 'Batches', link: '/batches', icon: 'mdi-beaker' },
        {
          title: 'Audit Logs',
          link: '/audit_logs',
          icon: 'mdi-shield-check',
          requiresOwner: true
        },
        {
          title: 'Accounts',
          link: '/accounts',
          icon: 'mdi-account-multiple-plus',
          requiresOwner: true
        },
        { title: 'Tips', link: '/tips', icon: 'mdi-lightbulb-on-outline' },
        { title: 'Settings', link: '/settings', icon: 'mdi-settings' }
      ]
    }
  },
  computed: {
    ...mapGetters('auth', ['keycloak']),
    // Reactive boolean sourced from data (populated via collectRoles).
    hasOwnerRole() {
      return this.userRoles.includes('Owner')
    },
    filteredSecondaryItems() {
      return this.secondaryItems.filter(
        (tool) => !tool.requiresOwner || this.hasOwnerRole
      )
    }
  },
  watch: {
    // Watch for changes in authentication state
    keycloak: {
      handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          console.log(
            'Sidebar: Authentication state changed, updating menu visibility'
          )
          // Force reactivity update
          this.$forceUpdate()
        }
      },
      deep: true
    },
    // Watch for Auth0 user changes
    '$auth0.user': {
      handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          this.$forceUpdate()
        }
      },
      deep: true
    },
    // Watch for Auth0 authentication state changes
    '$auth0.isAuthenticated': {
      handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          this.$forceUpdate()
        }
      }
    },
    drawerOpen(val) {
      if (!val) {
        // Ensure the drawer never drifts off-screen; keep it pinned open.
        this.$nextTick(() => {
          this.drawerOpen = true
        })
      }
    }
  },
  created() {
    // Force a check of authentication state on component mount
    this.$nextTick(() => {
      this.ensureDrawerVisible()
      this.collectRoles()
    })
    // In cases where auth initializes slightly after mount, re-try shortly.
    setTimeout(() => {
      this.ensureDrawerVisible()
      this.collectRoles()
    }, 500)
  },
  methods: {
    ensureDrawerVisible() {
      if (!this.drawerOpen) {
        this.drawerOpen = true
      }
    },
    toggleSidebar() {
      console.log('Toggling sidebar:', this.expanded ? 'collapse' : 'expand')
      this.ensureDrawerVisible()
      this.expanded = !this.expanded
      this.$emit(
        'item-clicked',
        this.expanded ? 'sidebar-expand' : 'sidebar-collapse'
      )
    },
    handleClick(link) {
      this.ensureDrawerVisible()
      this.$emit('item-clicked', link)
    },
    collectRoles() {
      let roles = []
      // Auth0 user roles
      try {
        if (this.$auth0 && this.$auth0.user) {
          const user = this.$auth0.user
          if (Array.isArray(user.roles)) {
            roles = roles.concat(user.roles)
          }
          if (user.app_metadata?.roles) {
            roles = roles.concat(user.app_metadata.roles)
          }
        }
      } catch (e) {
        console.warn('Auth0 role collection failed', e)
      }

      // Stored roles
      try {
        const storedRoles = localStorage.getItem('user_roles')
        if (storedRoles) {
          roles = roles.concat(JSON.parse(storedRoles))
        }
      } catch (e) {
        console.warn('Stored roles parse failed', e)
      }

      // Keycloak (may appear late; copy only if exists)
      try {
        if (this.keycloak?.tokenParsed) {
          const kc = this.keycloak.tokenParsed
          roles = roles.concat(
            kc.roles || [],
            kc.resource_access?.frontend?.roles || [],
            kc.realm_access?.roles || []
          )
        }
      } catch (e) {
        console.warn('Keycloak role collection failed', e)
      }

      // Token direct decode (non-reactive unless we call this method)
      try {
        const token = TokenService.getToken()
        if (token?.access_token) {
          const tokenPayload = JSON.parse(
            atob(token.access_token.split('.')[1])
          )
          if (tokenPayload.roles) {
            roles = roles.concat(tokenPayload.roles)
          } else if (tokenPayload.realm_access?.roles) {
            roles = roles.concat(tokenPayload.realm_access.roles)
          } else if (tokenPayload.resource_access) {
            roles = roles.concat(
              ...Object.values(tokenPayload.resource_access).map(
                (r) => r.roles || []
              )
            )
          }
        }
      } catch (e) {
        console.warn('Token role decode failed', e)
      }

      // De-duplicate
      const unique = [...new Set(roles.filter(Boolean))]
      // Only update (trigger reactivity) if changed
      if (unique.sort().join(',') !== this.userRoles.slice().sort().join(',')) {
        this.userRoles = unique
      }
    }
  }
}
</script>

<style scoped>
.sidebar {
  transition: width 0.3s;
  overflow-x: hidden;
  background-color: #000000 !important;
}
.header {
  font-size: 1.3rem;
  font-weight: bold;
  color: #ffffff;
}
.icon-regular {
  font-size: 24px !important;
}
.icon-small {
  font-size: 18px !important;
}
.item-title,
.small-title {
  font-size: 15px;
  color: #ffffff;
  font-family: 'Bell MT', sans-serif;
}
.footer {
  position: absolute;
  width: 100%;
  bottom: 0;
}
.divider {
  background-color: #444 !important;
}
.sidebar .v-list-item--link:hover {
  background-color: #333333 !important;
}
</style>
